import React, {
  DependencyList,
  EffectCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTabId } from './TabContext';

type TabContextType = {
  subscribeToOnTabFocused: (
    tabId: string,
    handler: EffectCallback,
    instanceId: string,
  ) => void;
};
export const TabContext = React.createContext<TabContextType>(null!);

export const TabContextProvider: React.FC<React.PropsWithChildren> = (
  props,
) => {
  const currentTabId = useTabId();
  const handlers = useRef<
    Record<
      string,
      | Record<
          string,
          {
            tabFocusedHandler: () => (() => void) | void;
            tabUnfocusedHandler: undefined | (() => void) | void;
          }
        >
      | undefined
    >
  >({});
  const value: TabContextType = useMemo(() => {
    return {
      subscribeToOnTabFocused(tabId, focusHandler, instanceId) {
        handlers.current[tabId] ??= {};
        handlers.current[tabId][instanceId]?.tabUnfocusedHandler?.();

        const unfocusHandler = focusHandler();

        handlers.current[tabId][instanceId] = {
          tabFocusedHandler: focusHandler,
          tabUnfocusedHandler: unfocusHandler,
        };
      },
    };
  }, []);

  const previousTabIdRef = useRef('');

  useEffect(
    function runOnFocusHandlers() {
      // run unfocus handlers for `previousTab`
      const previousTabId = previousTabIdRef.current;
      const tabHandlers = handlers.current[previousTabId!];
      if (tabHandlers) {
        Object.values(tabHandlers)?.forEach((x) => x?.tabUnfocusedHandler?.());
      }

      previousTabIdRef.current = currentTabId ?? '';
      const currentTabHandlers = handlers.current[currentTabId!];
      if (!currentTabHandlers) return;

      // run focus handlers for new tab
      Object.values(currentTabHandlers).forEach((x) => {
        x.tabUnfocusedHandler = x.tabFocusedHandler();
      });
    },
    [currentTabId],
  );
  return (
    <TabContext.Provider value={value}>{props.children}</TabContext.Provider>
  );
};

/*
 * Similar to useEffect but runs when a current tab is focused
 */
export function useTabFocusEffect(
  effect: EffectCallback,
  deps?: DependencyList,
  // this is a special field to fix the following issue:
  //    If you put some control as Editor in DxGrid, it will create a lot of editors without unmounting them (e.g. when you try to edit the same cell many times).
  //    So, if you call `useTabFocusEffect` from that Editor you will have a lot of instances at the same time.
  //    In case of Monaco Editor it will cause a lot of Mounts/Unmounts, while we actually need only 1.
  //
  //    So, you could use `instanceId` to treat all such instances as a single one.
  instanceId?: string,
) {
  const currentTabId = useTabId();
  const [localInstanceId] = useState(() => new Date().getTime().toString());
  const context = useContext(TabContext);
  useEffect(() => {
    context.subscribeToOnTabFocused(
      currentTabId!,
      effect,
      instanceId ?? localInstanceId,
    );
  }, deps);
}
